import ReactQuill from 'react-quill-new'
import v4 from 'uuid/v4'
import { normalizeMarkdownTableCell } from '../../../../utils/markdownTableParser'

// A table is block content, so the blot must be a BlockEmbed. The quill-1 era
// version was an inline Embed (span) styled display:block — under quill 2 the
// inline Embed's cursor-guard text nodes sit inside the contenteditable=false
// span, which breaks caret placement on the lines around the table (clicking
// above a pasted table could not put the cursor there). As a BlockEmbed the
// table owns its line and quill handles caret navigation around it natively.
const BlockEmbed = ReactQuill.Quill.import('blots/block/embed')

const DEFAULT_TABLE_DATA = { rows: [], alignments: [] }

const parseInlineFormatting = text => {
    if (!text) return [{ text: '', bold: false, italic: false, strike: false }]

    const segments = []
    let remaining = text

    const findNextMatch = str => {
        const matches = []
        const boldItalicMatch = /\*\*\*(.+?)\*\*\*/s.exec(str)
        if (boldItalicMatch) {
            matches.push({
                index: boldItalicMatch.index,
                length: boldItalicMatch[0].length,
                text: boldItalicMatch[1],
                bold: true,
                italic: true,
                strike: false,
            })
        }

        const boldMatch = /\*\*(.+?)\*\*/s.exec(str)
        if (boldMatch && (!boldItalicMatch || boldMatch.index < boldItalicMatch.index)) {
            const beforeBold = str.substring(0, boldMatch.index)
            const afterBold = str.substring(boldMatch.index + boldMatch[0].length)
            if (!beforeBold.endsWith('*') && !afterBold.startsWith('*')) {
                matches.push({
                    index: boldMatch.index,
                    length: boldMatch[0].length,
                    text: boldMatch[1],
                    bold: true,
                    italic: false,
                    strike: false,
                })
            }
        }

        let italicSearchStart = 0
        while (italicSearchStart < str.length) {
            const searchStr = str.substring(italicSearchStart)
            const italicMatch = /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/s.exec(searchStr)
            if (!italicMatch) break

            const actualIndex = italicSearchStart + italicMatch.index
            const before = str.substring(0, actualIndex)
            const after = str.substring(actualIndex + italicMatch[0].length)
            if (!before.endsWith('*') && !after.startsWith('*')) {
                matches.push({
                    index: actualIndex,
                    length: italicMatch[0].length,
                    text: italicMatch[1],
                    bold: false,
                    italic: true,
                    strike: false,
                })
                break
            }
            italicSearchStart = actualIndex + 1
        }

        const strikeMatch = /~~(.+?)~~/s.exec(str)
        if (strikeMatch) {
            matches.push({
                index: strikeMatch.index,
                length: strikeMatch[0].length,
                text: strikeMatch[1],
                bold: false,
                italic: false,
                strike: true,
            })
        }

        if (matches.length === 0) return null
        return matches.reduce((earliest, current) => (current.index < earliest.index ? current : earliest))
    }

    while (remaining.length > 0) {
        const match = findNextMatch(remaining)
        if (!match) {
            segments.push({ text: remaining, bold: false, italic: false, strike: false })
            break
        }

        if (match.index > 0) {
            segments.push({
                text: remaining.substring(0, match.index),
                bold: false,
                italic: false,
                strike: false,
            })
        }

        segments.push({
            text: match.text,
            bold: match.bold,
            italic: match.italic,
            strike: match.strike,
        })

        remaining = remaining.substring(match.index + match.length)
    }

    return segments.length > 0 ? segments : [{ text: '', bold: false, italic: false, strike: false }]
}

const appendFormattedText = (node, text) => {
    parseInlineFormatting(text).forEach(segment => {
        const span = document.createElement('span')
        span.textContent = segment.text
        if (segment.bold) span.style.fontWeight = '700'
        if (segment.italic) span.style.fontStyle = 'italic'
        if (segment.strike) span.style.textDecoration = 'line-through'
        node.appendChild(span)
    })
}

const createCell = (tagName, text, alignment) => {
    const cell = document.createElement(tagName)
    if (alignment) {
        cell.style.textAlign = alignment
    }
    appendFormattedText(cell, normalizeMarkdownTableCell(text))
    return cell
}

export default class MarkdownTableFormat extends BlockEmbed {
    static create(tableData = DEFAULT_TABLE_DATA) {
        const node = super.create()
        const id = tableData.id || v4()
        const rows = Array.isArray(tableData.rows) ? tableData.rows : []
        const alignments = Array.isArray(tableData.alignments) ? tableData.alignments : []

        node.setAttribute('data-id', id)
        // Legacy tables have no persisted id. Keep their value unchanged until the
        // first edit; edited tables retain their identity across embed replacements.
        node.setAttribute('data-markdown-table', JSON.stringify({ rows, alignments, ...(tableData.id ? { id } : {}) }))
        node.setAttribute('contenteditable', false)

        const scrollWrapper = document.createElement('div')
        scrollWrapper.className = 'ql-markdown-table-scroll'

        const table = document.createElement('table')
        table.className = 'ql-markdown-table'

        rows.forEach((row, rowIndex) => {
            const tableRow = document.createElement('tr')
            const cellTag = rowIndex === 0 ? 'th' : 'td'
            row.forEach((cellText, cellIndex) => {
                const cell = createCell(cellTag, cellText, alignments[cellIndex])
                cell.dataset.row = rowIndex
                cell.dataset.column = cellIndex
                tableRow.appendChild(cell)
            })
            table.appendChild(tableRow)
        })

        scrollWrapper.appendChild(table)
        node.appendChild(scrollWrapper)
        return node
    }

    static value(domNode) {
        try {
            return JSON.parse(domNode.getAttribute('data-markdown-table')) || DEFAULT_TABLE_DATA
        } catch (error) {
            return DEFAULT_TABLE_DATA
        }
    }
}

MarkdownTableFormat.blotName = 'markdownTable'
MarkdownTableFormat.className = 'ql-markdownTable'
MarkdownTableFormat.tagName = 'div'
